import { ReactNode } from "react";

import { useParams } from "react-router-dom";

import {
  StudentActionFeedState,
  useAssessmentQuery,
  useWorkGroupQuery,
} from "@/generated/graphql";

import { ActionFeed, StudentActionFeedPayload } from "./action-feed";
import { GroupCard } from "./group-card";
import { GroupCharterCard } from "./group-charter-card";
import { HomeOutletContext } from "./home-page";
import { InstructionsRailCard } from "./instructions-rail-card";
import * as styles from "./student-home.css";

/**
 * The `student_home` replacement for `Preparation`. The feed payload arrives
 * from the single query lifted to `HomePage` (via `HomeOutletContext`); this
 * page still reads the assessment and work group directly.
 *
 * `ActionFeed` is presentational and has no query-error handling of its own
 * (see its module doc), so this page synthesises an `UNKNOWN` fallback payload
 * on a hard feed error. Without it, a failed request would fall through to the
 * rail-alone layout below, which reads as "you have nothing to do" rather than
 * "we couldn't check".
 */
export function StudentHome({
  studentActionFeed: insights,
  networkStatus,
  error: insightsError,
  loading: insightsLoading,
  workGroupRefreshing,
}: HomeOutletContext) {
  const { assessmentId = "" } = useParams();
  const { data: assessmentData } = useAssessmentQuery({
    variables: { assessmentId },
  });
  const { data: workGroupData, loading: workGroupLoading } =
    useWorkGroupQuery({
      variables: { assessmentId },
    });

  const assessment = assessmentData?.assessment;
  const workGroup = workGroupData?.studentWorkGroup;
  const group = workGroup?.group;

  if (!assessment) {
    return null;
  }

  // The charter card only shows once the charter is usable: enabled and a
  // published form exists. The backend folds both conditions into
  // `groupCharterAvailable`.
  const showCharter = assessment.settings?.groupCharterAvailable ?? false;

  // `GroupCard`'s only copy for a missing `userGroup` lives in its
  // self-enrolment branches; a teacher-assigned group with no `userGroup`
  // yet has no copy of its own there and falls into the self-enrolment-off
  // branch, which assumes a group always exists and would render a literal
  // "undefined" in its place. That combination is only ever true while
  // `workGroupData` hasn't loaded, or briefly after it has if the teacher
  // hasn't assigned this student a group yet, so the card is deferred
  // rather than reached with nothing to say.
  // Two reads of the same document and neither sees the other: this page's own
  // cache-first query, and `HomePage`'s network-only refresh, which is the one
  // still in flight on a warm re-entry. Both charter CTAs wait on the pair,
  // since either can create a charter the group already has.
  const workGroupPending = workGroupLoading || workGroupRefreshing;

  const selfEnrolled = assessment.settings?.groupSelfEnrollmentEnabled === true;
  const showGroupCard = !workGroupLoading && (selfEnrolled || group != null);

  // Real insights win. A hard error falls back to a synthesised `UNKNOWN`
  // payload so `ActionFeed` still has something to render its "can't check
  // everything" copy from. A genuine `null` (not a group assessment, no due
  // date, or outside the stage window) leaves `feedInsights` `undefined`, the
  // same as loading. `insightsLoading` below is what tells the two apart.
  const feedInsights: StudentActionFeedPayload | undefined = insights
    ? insights
    : insightsError
      ? {
          __typename: "StudentActionFeed",
          // None of these are read on this path: `ActionFeed` never consumes
          // `badgeCount`, and the rail below reads viewed state from
          // `insights` directly rather than `feedInsights`. All exist only
          // to satisfy `StudentActionFeedPayload`.
          state: StudentActionFeedState.Unknown,
          badgeCount: 0,
          actions: [],
          waitingOnOthers: [],
          groupWorkViewed: null,
          individualWorkViewed: null,
          peerEvaluationViewed: null,
        }
      : undefined;

  const rail = (
    // A distinct landmark from ActionFeed's `role="region"`, so a
    // screen-reader user can move between the two Home columns.
    <div
      className={styles.rail}
      data-testid="student-home-rail"
      role="region"
      aria-label="Your group and work"
    >
      {showGroupCard && (
        <RailSection>
          <GroupCard
            assessment={assessment}
            workGroupId={workGroup?.id}
            userGroup={group ?? null}
            finalised={workGroup?.finalised ?? false}
            changeRequested={workGroup?.latestGroupChangeRequest}
            layout="stacked"
            title="Your group"
          />
        </RailSection>
      )}
      {group && showCharter && (
        <RailSection>
          <GroupCharterCard
            groupId={group.id}
            charter={workGroup?.charter}
            finalised={workGroup?.finalised}
            dueDate={assessment.settings?.groupCharterDueDate}
            workGroupPending={workGroupPending}
            layout="stacked"
            title="Group charter"
          />
        </RailSection>
      )}
      <RailSection>
        <InstructionsRailCard
          assessment={assessment}
          groupWorkSettings={workGroup?.work?.settings}
          individualWorkSettings={workGroup?.individualWork?.settings}
          // The rail card owns the null/undefined distinction from here:
          // raw per-work values while a payload exists, `undefined` (not a
          // collapsed guess) while there is none to read, whether that's
          // still loading or a hard error.
          viewed={
            insights
              ? {
                  group: insights.groupWorkViewed,
                  individual: insights.individualWorkViewed,
                  peerEvaluation: insights.peerEvaluationViewed,
                }
              : undefined
          }
        />
      </RailSection>
    </div>
  );

  // Only a settled `null` takes the rail-alone layout, not the loading state.
  // Loading falls through to the grid below with its feed slot empty instead,
  // because the rail-alone `div` and the grid's `Fragment` are different root
  // element types: returning the rail-alone branch during loading would make
  // React tear down and rebuild the whole rail on every cold load, remounting
  // `GroupCard` and re-running its email-consent and `ResizeObserver` setup
  // the instant the query resolves.
  if (!insightsLoading && !feedInsights) {
    return rail;
  }

  return (
    <>
      <div className={styles.grid} data-testid="student-home-grid">
        {feedInsights ? (
          <ActionFeed
            insights={feedInsights}
            networkStatus={networkStatus}
            charterId={workGroup?.charter?.id}
            groupId={group?.id}
            workGroupPending={workGroupPending}
            groupFinalised={workGroup?.finalised}
          />
        ) : (
          // `grid`'s two fixed tracks (student-home.css.ts) fill by
          // auto-placement in document order. With no first item while
          // loading, `rail` would become the grid's only item and get
          // auto-placed into the fluid first track instead of the 400px
          // second one it belongs in, going full-width rather than just
          // losing its styling. This placeholder keeps two items in the
          // grid at all times so `rail`'s track never changes. Only this
          // slot, not `rail`'s, is what `feedInsights` resolving replaces.
          <div />
        )}
        {rail}
      </div>
    </>
  );
}

// This wrapper exists only to let a rail row shrink to the 400px column
// instead of flooring at its card's content width (student-home.css.ts).
function RailSection({ children }: { children: ReactNode }) {
  return <div className={styles.railSection}>{children}</div>;
}
